import React, { memo, useEffect } from 'react';
import { prop } from 'ramda';
import { useForm } from 'react-final-form';
import { clsx } from 'js/utils/classNames.util';
import { useFormatter } from 'js/hooks/useFormatter';
import { Select } from 'js/components/common/Fields/Select';
import { useComponentTranslations } from 'js/hooks/component';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { EditableDropDown } from 'js/components/common/Fields/EditableDropDown';

import * as Types from './types';
import * as hooks from './hooks';
import { getLimitActions } from '../../helpers';
import { IMS_UNLIMITED_AMOUNT } from './constants';
import { useDynamicReducerSelector } from '../../store';
import { useFieldLabel } from '../ChangeFormField/hooks';

import style from './Limits.scss';

export const Limits: React.FC<Types.ILimitsOptions> = memo(({ type, field, fieldId, className }) => {
    const { batch, change } = useForm();
    const formatter = useFormatter();
    const limitsConfig = hooks.useLimitsConfig(field);
    const translate = useComponentTranslations();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const limitsCurrency = useDynamicReducerSelector(prop('limitsCurrency'));
    const betLossLimitActions = useAppSelector(prop('betLossLimitActions'));
    const limitActions = getLimitActions(betLossLimitActions[`${type}limitActions`]);
    const isLimitsActionsVisible = limitActions.length >= 2;
    const { infoTooltip: fieldTooltip, imsFieldSession, imsFieldDaily, imsFieldWeekly, imsFieldMonthly, imsFieldAction } = field;

    let isLimitsEnabled = false;

    if (limitsConfig && 'betLimitEnabled' in limitsConfig) {
        isLimitsEnabled = limitsConfig.betLimitEnabled;
    } else if (limitsConfig && 'lossLimitEnabled' in limitsConfig) {
        isLimitsEnabled = limitsConfig.lossLimitEnabled;
    }

    const hideLimits = isLoggedIn || !limitsConfig || !isLimitsEnabled || !limitsCurrency;
    const infoTooltip = fieldTooltip && translate(fieldTooltip);

    const isBetLimit = type === 'bet';
    const limitPrefix = `playtech.mobile.system.form.limits.${type}Limit`;
    const placeholder = translate('playtech.mobile.system.form.limits.placeholder');
    const [actionLabel, getActionLabelText] = useFieldLabel(`${limitPrefix}.action`);
    const [sessionLabel, getSessionLabelText] = useFieldLabel(`${limitPrefix}.sessionLimit`);
    const [dayLabel, getDayLabelText] = useFieldLabel(`${limitPrefix}.${isBetLimit ? 'dayLimit' : 'dailyLimit'}`);
    const [weekLabel, getWeekLabelText] = useFieldLabel(`${limitPrefix}.${isBetLimit ? 'weekLimit' : 'weeklyLimit'}`);
    const [monthLabel, getMonthLabelText] = useFieldLabel(`${limitPrefix}.${isBetLimit ? 'monthLimit' : 'monthlyLimit'}`);
    const maxValue = Boolean(limitsConfig?.noLimitsAllowed) && IMS_UNLIMITED_AMOUNT;

    useEffect(() => {
        if (hideLimits) {
            return;
        }

        const {
            isSessionEnabled, isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
            sessionDefaultLimit, dailyDefaultLimit, weeklyDefaultLimit, monthlyDefaultLimit
        } = limitsConfig;

        batch(() => {
            isSessionEnabled && change(imsFieldSession, sessionDefaultLimit);
            isDailyEnabled && change(imsFieldDaily, dailyDefaultLimit);
            isWeeklyEnabled && change(imsFieldWeekly, weeklyDefaultLimit);
            isMonthlyEnabled && change(imsFieldMonthly, monthlyDefaultLimit);
        });

        // We need to add limitsCurrency to the dependencies so that when the currency changes,
        // the limits fields are cleared regardless of whether the input data changes or not.
    }, [
        batch, change, hideLimits, limitsConfig, limitsCurrency,
        imsFieldSession, imsFieldDaily, imsFieldWeekly, imsFieldMonthly
    ]);

    if (hideLimits) {
        return null;
    }

    const {
        isSessionEnabled, isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
        mode, dataList, displayValuesMap
    } = limitsConfig;

    return (
        <fieldset className={clsx([className, style.limits, `cms-form-${type}-limits`])}>
            {isLimitsActionsVisible
                ? (
                    <Select
                        label={actionLabel}
                        name={imsFieldAction}
                        infoTooltip={infoTooltip}
                        className={style.control}
                        id={`${fieldId}_${type}-limit-action`}
                        placeholder={translate(`${limitPrefix}.action`)}
                        aria-label={getActionLabelText() || translate('playtech.mobile.system.select.aria-label')}
                        options={limitActions.map(action => ({
                            value: action,
                            label: translate(`${limitPrefix}.action.${action}`)
                        }))}
                    />
                )
                : <input type="hidden" name={imsFieldAction} />
            }

            {isSessionEnabled && (
                <EditableDropDown
                    mode={mode}
                    inputMode="decimal"
                    dataList={dataList}
                    label={sessionLabel}
                    name={imsFieldSession}
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_${type}-session-limit`}
                    aria-label={getSessionLabelText() || placeholder}
                    infoTooltip={!isLimitsActionsVisible && infoTooltip}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                />
            )}
            {isDailyEnabled && (
                <EditableDropDown
                    mode={mode}
                    label={dayLabel}
                    inputMode="decimal"
                    dataList={dataList}
                    name={imsFieldDaily}
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_${type}-daily-limit`}
                    aria-label={getDayLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                    infoTooltip={!isLimitsActionsVisible && !isSessionEnabled && infoTooltip}
                />
            )}
            {isWeeklyEnabled && (
                <EditableDropDown
                    mode={mode}
                    label={weekLabel}
                    inputMode="decimal"
                    dataList={dataList}
                    name={imsFieldWeekly}
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_${type}-weekly-limit`}
                    aria-label={getWeekLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                    infoTooltip={!isLimitsActionsVisible && !isSessionEnabled && !isDailyEnabled && infoTooltip}
                />
            )}
            {isMonthlyEnabled && (
                <EditableDropDown
                    mode={mode}
                    label={monthLabel}
                    inputMode="decimal"
                    dataList={dataList}
                    name={imsFieldMonthly}
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_${type}-monthly-limit`}
                    aria-label={getMonthLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                    infoTooltip={
                        !isLimitsActionsVisible && !isSessionEnabled && !isDailyEnabled && !isWeeklyEnabled && infoTooltip
                    }
                />
            )}
        </fieldset>
    );
});
